import { Component, inject, OnDestroy, OnInit, SecurityContext } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { DomSanitizer } from '@angular/platform-browser';
import { firstValueFrom, Subscription } from 'rxjs';
import { BasicError } from 'projects/shared/lib/error/basic-error';
import { ICartPreviewEnvironment } from 'projects/shared/lib/interfaces/environment/cart/cart-preview-environment.interface';
import { LoggerService } from 'projects/shared/lib/services/infrastructure/logger/logger.service';
import {
  ShoppingCartDto, ShoppingCartItemDto, ProductIdentifierDto, ProductDetailDto,
  ProductImageDto, ProductAvailabilityDto, PriceDto, ProductAvailabilityGetDto, EImageSize, EError
} from 'projects/shared/lib/services/INTEGRA_Shop_Client';
import { ENVIRONMENT } from 'projects/shared/lib/environment/environment';
import { CartPreviewItem } from './item/cart-preview-item.model';
import { ICartService, ICartServiceToken } from 'projects/shared/lib/services/data/cart/cart-service.interface';
import { ServiceLocator } from 'projects/shared/lib/services/infrastructure/service-locator.service';
import { PriceFormat } from '../../price/format/price-format.model';
import { ELoadingIndicatorStyle } from 'projects/shared/lib/enum/loading/loading-indicator-style.enum';
import { IAuthService, IAuthServiceToken } from 'projects/shared/lib/services/infrastructure/authentication/auth-service.interface';
import { IProductService, IProductServiceToken } from 'projects/shared/lib/services/data/product/product-service.interface';
import { ErrorCodeService } from 'projects/shared/lib/services/infrastructure/error-code.service';
import { ISalesUnitService, ISalesUnitServiceToken } from 'projects/shared/lib/services/infrastructure/salesunit/salesunit-service.interface';
import { IConfigurationService, IConfigurationServiceToken } from 'projects/shared/lib/services/infrastructure/configuration/configuration-service.interface';
import { IComparisonService, IComparisonServiceToken } from 'projects/shared/lib/services/infrastructure/comparison/comparison-service.interface';
import { CartPreviewItemComponent } from './item/cart-preview-item.component';
import { PriceSimpleComponent } from '../../price/simple/price-simple.component';
import { PriceMissingInfoIconComponent } from '../../price/price-missing-info-icon/price-missing-info-icon.component';

@Component({
  selector: 'app-cart-preview',
  imports: [
    CommonModule,
    RouterModule,
    CartPreviewItemComponent,
    PriceSimpleComponent,
    PriceMissingInfoIconComponent
  ],
  templateUrl: './cart-preview.component.html',
  styleUrls: ['./cart-preview.component.scss']
})
export class CartPreviewComponent implements OnInit, OnDestroy {
  private readonly salesUnitService: ISalesUnitService = inject(ISalesUnitServiceToken);
  private readonly configurationService: IConfigurationService = inject(IConfigurationServiceToken);

  public isAuthenticated: boolean | undefined;

  public cart: ShoppingCartDto | undefined;
  public cartItemCount: number;
  public cartPreviewItemList: CartPreviewItem[];
  public cartPreviewItemInactiveList: ProductIdentifierDto[] | undefined;

  public cartPreviewDisplayTotalPrice: boolean;

  public i18nShoppingCartPreviewCartInfo: { [k: string]: string } = {
    '=0': $localize`:@@ShoppingCartPreviewCartInfo0:No items in cart`,
    '=1': $localize`:@@ShoppingCartPreviewCartInfo1:1 item in cart`,
    'other': $localize`:@@ShoppingCartPreviewCartInfoOther:# items in cart`
  };

  private cartItemCountSubscription: Subscription | undefined;
  private cartSubscription: Subscription | undefined;
  private maximumOrderAmount: number;
  protected authenticationSusbcription: Subscription | undefined;

  public shouldLoadPrices: boolean | undefined;
  public totalPrice: PriceDto | undefined;

  private cartPreviewSettings: ICartPreviewEnvironment | undefined;

  protected logger: LoggerService;
  protected readonly cartDataService: ICartService;
  protected readonly comparisonService: IComparisonService;
  protected readonly productService: IProductService;
  private sanitizer: DomSanitizer;
  protected readonly authService: IAuthService;

  public eLoadingIndicatorStyle: typeof ELoadingIndicatorStyle;
  public isLoading: boolean | undefined;

  public constructor() {
    this.logger = ServiceLocator.injector.get(LoggerService);
    this.cartDataService = inject(ICartServiceToken);
    this.comparisonService = inject(IComparisonServiceToken);
    this.productService = inject(IProductServiceToken);
    this.sanitizer = ServiceLocator.injector.get(DomSanitizer);
    this.authService = inject(IAuthServiceToken);

    this.cartPreviewSettings = ENVIRONMENT.cart.cartPreview;
    this.cartItemCount = 0;
    this.cartPreviewItemList = [];
    this.eLoadingIndicatorStyle = ELoadingIndicatorStyle;
    this.maximumOrderAmount = ENVIRONMENT.checkout.maximumOrderAmount;

    this.cartPreviewDisplayTotalPrice = ENVIRONMENT.cart.cartPreview?.cartPreviewDisplayTotalPrice ?? true;
  }

  public ngOnInit(): void {
    this.authenticationSusbcriptionInit();
    this.cartItemCountSubscriptionInit();
    this.cartSubscriptionInit();
    this.checkoutConfigurationCheckAsync();
  }

  public ngOnDestroy(): void {
    this.authenticationSusbcription?.unsubscribe();
    this.cartItemCountSubscription?.unsubscribe();
    this.cartSubscription?.unsubscribe();
  }

  authenticationSusbcriptionInit() {
    if (!this.authenticationSusbcription) {
      this.authenticationSusbcription = this.authService.authenticatedGet().subscribe(
        (isAuthenticated) => {
          this.isAuthenticated = isAuthenticated;
          this.shouldLoadPrices = this.shouldLoadPricesCurrentStateGet();
        }
      );
    }
  }

  cartItemCountSubscriptionInit() {
    if (!this.cartItemCountSubscription) {
      this.cartItemCountSubscription =
        this.cartDataService.cartCurrentItemCountChangedGet().subscribe(
          (cartItemCount: number) => {
            this.cartItemCount = cartItemCount;
          }
        );
    }
  }

  cartSubscriptionInit() {
    if (!this.cartSubscription) {
      this.cartSubscription =
        this.cartDataService.cartCurrentChangedGet().subscribe(
          (cart: ShoppingCartDto | undefined) => {

            if (cart == undefined) {
              return this.reset();
            }

            this.cartUpdate(cart);
          }
        );
    }
  }

  protected shouldLoadPricesCurrentStateGet(): boolean {
    return ENVIRONMENT.price.allowAnonymousPrice || this.authService.isAuthenticated();
  }

  protected cartUpdate(cart: ShoppingCartDto) {
    this.cart = cart;

    this.cartPreviewItemInactiveList = cart.inactiveProductList;

    if (cart?.totalPrice != undefined) {
      this.totalPrice = cart.totalPrice;
    }

    let localList: CartPreviewItem[] = [];
    Object.assign(localList, this.cartPreviewItemList);

    // lookup for known (already displayed items)
    localList.forEach(
      (cartPreviewItem: CartPreviewItem) => {
        let match: ShoppingCartItemDto | undefined = cart?.itemList?.find(
          cartItem => this.comparisonService.equalsProductIdentifierByNumberOrGuid(cartItem.productIdentifier, cartPreviewItem.productIdentifier)
        );

        if (!match) {
          // remove
          let index = this.cartPreviewItemList.findIndex(item => item == cartPreviewItem);
          this.cartPreviewItemList.splice(index, 1);
        } else {
          // set reference and update
          cartPreviewItem.shoppingCartItem = match;
          if (match.totalPrice != undefined) {
            cartPreviewItem.shoppingCartItem = match;
            cartPreviewItem.priceFormat = new PriceFormat(match.totalPrice, match.orderUnit);
          }
        }
      }
    );

    // mapping for new (desired displayed items)
    let listToLoad: CartPreviewItem[] = [];
    cart?.itemList?.forEach(
      (cartItem: ShoppingCartItemDto) => {
        let match: CartPreviewItem | undefined = localList.find(
          (cartPreviewItem) => this.comparisonService.equalsProductIdentifierByNumberOrGuid(cartPreviewItem.productIdentifier, cartItem.productIdentifier)
        );

        if (!match) {
          // not yet displayed -> add
          let cartPreviewitem = this.cartPreviewItemGetByCartItem(cartItem);
          listToLoad.push(cartPreviewitem);
        }
      }
    );

    this.cartPreviewItemListNewlyAddedInit(listToLoad);
  }

  protected cartPreviewItemGetByCartItem(cartItem: ShoppingCartItemDto) {
    let priceFormat: PriceFormat | undefined;
    if (cartItem.totalPrice != undefined) {
      priceFormat = new PriceFormat(cartItem.totalPrice, cartItem.orderUnit);
    }
    let cartPreviewitem: CartPreviewItem = new CartPreviewItem(
      this.configurationService,
      {
        productIdentifier: cartItem.productIdentifier,
        shoppingCartItem: cartItem,
        priceFormat: priceFormat,
        minimumOrderAmount: 1,
        maximumOrderAmount: this.maximumOrderAmount,
        orderUnit: cartItem.orderUnit,
      }
    );
    return cartPreviewitem;
  }

  protected cartPreviewItemListNewlyAddedInit(listToLoad: CartPreviewItem[]) {
    this.cartPreviewItemList.push(...listToLoad);
    this.productDetailListLoadAsync(listToLoad);
    this.productThumbnailListLoadAsync(listToLoad);
    this.productAvailabilityListLoadAsync(listToLoad);
  }

  private reset() {
    this.cart = undefined;
    this.cartItemCount = 0;
    this.cartPreviewItemList = [];
    this.cartPreviewItemInactiveList = undefined;
    this.totalPrice = undefined;
    this.isLoading = false;
    return;
  }

  private async productDetailListLoadAsync(cartPreviewItemList: CartPreviewItem[]) {
    if (!cartPreviewItemList || cartPreviewItemList.length <= 0) {
      return;
    }
    let productIdentifierList: ProductIdentifierDto[] = [];

    cartPreviewItemList.forEach(
      (shoppingCartPreviewEntry: CartPreviewItem) => {
        let productIdentifier: ProductIdentifierDto | undefined = shoppingCartPreviewEntry.productIdentifier;
        if (!productIdentifier) {
          throw new Error('Missing productIdentifier upon loading data in comparison preview');
        }
        productIdentifierList.push(productIdentifier);
      }
    );

    await firstValueFrom(this.productService.productDetailGet(false, true, true, false, false, false, false, undefined, productIdentifierList)).then(async (result) => {
      let productDetailDtoList: ProductDetailDto[] | undefined = result.productList;
      let maximumOrderAmount = await this.checkoutConfigurationCheckAsync();

      cartPreviewItemList.forEach(
        (shoppingCartPreviewItem: CartPreviewItem) => {
          if (!productDetailDtoList || productDetailDtoList.length < 1) {
            return;
          }

          let productDetailDto: ProductDetailDto | undefined = productDetailDtoList.find(
            (productDetail: ProductDetailDto) => this.comparisonService.equalsProductIdentifierByNumberOrGuid(productDetail.productIdentifier, shoppingCartPreviewItem.productIdentifier)
          );

          shoppingCartPreviewItem.description = productDetailDto?.description;
          shoppingCartPreviewItem.minimumOrderAmount = productDetailDto?.minimumOrderAmount ? productDetailDto?.minimumOrderAmount : 1;
          shoppingCartPreviewItem.maximumOrderAmount = this.maximumOrderAmount;
          if (shoppingCartPreviewItem.orderUnit == undefined) {
            let salesUnitDefault = this.salesUnitService.defaultSalesUnitGet(productDetailDto?.salesUnitList);
            shoppingCartPreviewItem.orderUnit = salesUnitDefault;
          }
        }
      );
    }).catch((error) => {
      this.logger.error(error, this.constructor.name);
      let errorMessage = $localize`:@@CartPreviewBaseProductLoadErrorMessage:Could not load products`;
      throw new BasicError(errorMessage, JSON.parse(error.response).errorCode);
    });
  }

  private async checkoutConfigurationCheckAsync() {
    let checkoutConfiguration = await firstValueFrom(ServiceLocator.injector.get(IConfigurationServiceToken).checkoutConfigurationGet());
    if (checkoutConfiguration.maximumOrderAmount != undefined) {
      this.maximumOrderAmount = checkoutConfiguration.maximumOrderAmount;
    }
  }

  private async productThumbnailListLoadAsync(cartPreviewItemList: CartPreviewItem[]) {
    let productIdentifierList: ProductIdentifierDto[] = cartPreviewItemList.map(
      (shoppingCartPreviewEntry: CartPreviewItem) => {
        let productIdentifier: ProductIdentifierDto | undefined = shoppingCartPreviewEntry.productIdentifier;
        if (!productIdentifier) {
          throw new BasicError('Missing productIdentifier upon loading data in comparison preview', ErrorCodeService.formatErrorCode(EError.F170090));
        }
        return productIdentifier;
      }
    );

    let thumbnailSize = this.cartPreviewSettings?.productThumbnailSize;

    if (thumbnailSize == undefined) {
      thumbnailSize = EImageSize.Medium;
    }

    await firstValueFrom(this.productService.productThumbnailGet(thumbnailSize, productIdentifierList)).then((result) => {
      let productImageDtoList: ProductImageDto[] | undefined = result.productList;
      cartPreviewItemList.forEach(
        (shoppingCartPreviewItem: CartPreviewItem) => {
          if (!productImageDtoList || productImageDtoList.length < 1) {
            return;
          }

          let thumbnailDto: ProductImageDto | undefined = productImageDtoList.find(
            (productImage: ProductImageDto) => this.comparisonService.equalsProductIdentifierByNumberOrGuid(productImage.productIdentifier, shoppingCartPreviewItem.productIdentifier)
          );

          if (!thumbnailDto) {
            this.logger.info(`Thumbnail for product#: '${shoppingCartPreviewItem.productIdentifier?.productNumber}' could not be loaded.`, this.constructor.name);
            return;
          }

          let sanitizedThumbnail = this.sanitizer.sanitize(SecurityContext.URL, `data:${thumbnailDto.contentType};base64,${thumbnailDto.content}`);
          if (!sanitizedThumbnail) {
            return;
          }

          shoppingCartPreviewItem.thumbnail = sanitizedThumbnail;
        }
      );
    }).catch((ex) => {
      this.logger.warn(ex, this.constructor.name);
    });
  }

  private async productAvailabilityListLoadAsync(cartPreviewItemList: CartPreviewItem[]) {
    let skipAvailabilityLoading = ENVIRONMENT.availability.disableAvailability;
    if (skipAvailabilityLoading == true) {
      return;
    }

    let productAvailabilityGetList: ProductAvailabilityGetDto[] = cartPreviewItemList.map(
      (shoppingCartPreviewEntry: CartPreviewItem) => new ProductAvailabilityGetDto({
        productIdentifier: shoppingCartPreviewEntry.productIdentifier,
        quantityUnit: shoppingCartPreviewEntry.orderUnit?.quantityUnit
      })
    );

    await firstValueFrom(this.productService.productAvailabilityGet({ productList: productAvailabilityGetList })).then(
      (productList) => {
        let productAvailabilityList: ProductAvailabilityDto[] | undefined = productList;
        cartPreviewItemList.forEach(
          (shoppingCartPreviewItem: CartPreviewItem) => {
            if (!productAvailabilityList || productAvailabilityList.length < 1) {
              return;
            }

            let productAvailability: ProductAvailabilityDto | undefined = productAvailabilityList.find(
              (availability: ProductAvailabilityDto) => this.comparisonService.equalsProductIdentifierByNumberOrGuid(availability.productIdentifier, shoppingCartPreviewItem.productIdentifier)
            );

            shoppingCartPreviewItem.availability = productAvailability;
          }
        );
      }).catch((error) => {
        this.logger.error(error, this.constructor.name);
        let errorMessage = $localize`:@@CartPreviewBaseAvailabilityLoadErrorMessage:Could not load product availabilities`;
        throw new BasicError(errorMessage, JSON.parse(error.response).errorCode);
      });
  }

}
